import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import { APP_CONFIG } from '../types'
import './Layout.css'

export default function Layout() {
    const { user, isAdmin, signIn, signOut } = useAuth()
    const { getItemCount } = useCart()
    const location = useLocation()
    const itemCount = getItemCount()

    return (
        <div className="layout">
            {/* Header */}
            <header className="header">
                <div className="container">
                    <div className="header-content">
                        {/* Logo */}
                        <Link to="/" className="logo">
                            <span className="logo-icon">👗</span>
                            <div className="logo-text">
                                <span className="logo-name">{APP_CONFIG.STORE_NAME}</span>
                                <span className="logo-tagline">{APP_CONFIG.STORE_LOCATION}</span>
                            </div>
                        </Link>

                        {/* Navigation */}
                        <nav className="nav-main">
                            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
                                Shop
                            </Link>
                            {user && (
                                <>
                                    <Link to="/orders" className={`nav-link ${location.pathname === '/orders' ? 'active' : ''}`}>
                                        Orders
                                    </Link>
                                    <Link to="/wallet" className={`nav-link ${location.pathname === '/wallet' ? 'active' : ''}`}>
                                        <span className="nav-coins">🪙</span>
                                        S-Coins
                                    </Link>
                                </>
                            )}
                            {isAdmin && (
                                <Link to="/admin" className="nav-link nav-admin">
                                    Admin
                                </Link>
                            )}
                        </nav>

                        {/* Right side */}
                        <div className="header-actions">
                            {/* Cart */}
                            <Link to="/cart" className="cart-btn">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                                    <line x1="3" y1="6" x2="21" y2="6"></line>
                                    <path d="M16 10a4 4 0 0 1-8 0"></path>
                                </svg>
                                {itemCount > 0 && (
                                    <span className="cart-badge">{itemCount}</span>
                                )}
                            </Link>

                            {/* User */}
                            {user ? (
                                <div className="user-menu">
                                    <Link to="/profile" className="user-avatar-link">
                                        <img
                                            src={user.picture}
                                            alt={user.name}
                                            className="avatar"
                                            referrerPolicy="no-referrer"
                                        />
                                    </Link>
                                    <div className="user-dropdown">
                                        <div className="user-info">
                                            <img
                                                src={user.picture}
                                                alt={user.name}
                                                className="avatar avatar-lg"
                                                referrerPolicy="no-referrer"
                                            />
                                            <div>
                                                <p className="user-name">{user.name}</p>
                                                <p className="user-email">{user.email}</p>
                                            </div>
                                        </div>
                                        <div className="user-scoins">
                                            <span className="scoins-icon">🪙</span>
                                            <span>{user.s_coins || 0} S-Coins</span>
                                        </div>
                                        <hr />
                                        <Link to="/profile" className="dropdown-link">Profile</Link>
                                        <Link to="/orders" className="dropdown-link">My Orders</Link>
                                        <Link to="/wallet" className="dropdown-link">S-Coins Wallet</Link>
                                        {isAdmin && <Link to="/admin" className="dropdown-link">Admin Panel</Link>}
                                        <hr />
                                        <button onClick={signOut} className="dropdown-link logout">
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button onClick={signIn} className="btn btn-primary">
                                    Sign In
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="main-content">
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <span className="logo-icon">👗</span>
                            <span>{APP_CONFIG.STORE_NAME}</span>
                        </div>
                        <p className="footer-location">📍 {APP_CONFIG.STORE_LOCATION}</p>
                        <p className="footer-tagline">{APP_CONFIG.STORE_TAGLINE}</p>
                    </div>
                    <p className="footer-copyright">
                        © {new Date().getFullYear()} {APP_CONFIG.STORE_NAME}. All rights reserved.
                    </p>
                </div>
            </footer>

            {/* Mobile Navigation */}
            <nav className="nav-mobile">
                <Link to="/" className={`nav-mobile-item ${location.pathname === '/' ? 'active' : ''}`}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                    <span>Shop</span>
                </Link>
                <Link to="/cart" className={`nav-mobile-item ${location.pathname === '/cart' ? 'active' : ''}`}>
                    <div className="nav-mobile-cart">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                            <line x1="3" y1="6" x2="21" y2="6"></line>
                            <path d="M16 10a4 4 0 0 1-8 0"></path>
                        </svg>
                        {itemCount > 0 && <span className="cart-badge-mobile">{itemCount}</span>}
                    </div>
                    <span>Cart</span>
                </Link>
                {user && (
                    <Link to="/wallet" className={`nav-mobile-item ${location.pathname === '/wallet' ? 'active' : ''}`}>
                        <span style={{ fontSize: '24px' }}>🪙</span>
                        <span>S-Coins</span>
                    </Link>
                )}
                <Link to={user ? '/profile' : '/'} className={`nav-mobile-item ${location.pathname === '/profile' ? 'active' : ''}`} onClick={user ? undefined : signIn}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    <span>{user ? 'Profile' : 'Sign In'}</span>
                </Link>
            </nav>
        </div>
    )
}
